/**
 * CollectionShareButton - Share button for collection context
 * Feature: 080-critique-sharing
 * Tasks: T085, T086
 *
 * Enables sharing from a collection context by:
 * 1. Using the most recent critique session if one exists
 * 2. Automatically creating a critique session if none exists
 * 3. Opening the share dialog for the selected/created critique
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/atoms';
import { ShareIcon } from '@/components/icons';
import { ShareDialog } from '@/components/ShareDialog';
import { useCreateCritiqueSessionGraphQL } from '@/hooks/graphql/useCollectionMutationsGraphQL';
import { useApolloClient } from '@apollo/client';
import logger from '@/utils/logger';

// ============================================================================
// Types
// ============================================================================

export interface CollectionSession {
  id: string;
  slug: string;
  title: string;
  createdAt: string;
}

export interface CollectionShareButtonProps {
  /** Collection global ID */
  collectionId: string;
  /** Collection name for creating new critique sessions */
  collectionName: string;
  /** Collection slug for URL construction */
  collectionSlug: string;
  /** Existing critique sessions for this collection */
  sessions?: CollectionSession[];
  /** Additional CSS classes */
  className?: string;
  /** Variant for button styling */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
}

// ============================================================================
// Component
// ============================================================================

export function CollectionShareButton({
  collectionId,
  collectionName,
  collectionSlug,
  sessions = [],
  className,
  variant = 'secondary',
  size = 'md',
}: CollectionShareButtonProps) {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [activeCritiqueSession, setActiveCritiqueSession] = useState<{
    id: string;
    slug: string;
  } | null>(null);

  const { createCritiqueSession } = useCreateCritiqueSessionGraphQL();
  const apolloClient = useApolloClient();

  /**
   * Get the most recent critique session from the list
   * Sorts by createdAt descending and returns the first one
   */
  const getMostRecentSession = useCallback((): CollectionSession | null => {
    if (!sessions || sessions.length === 0) return null;

    const sorted = [...sessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return sorted[0];
  }, [sessions]);

  /**
   * Handle share button click
   * - If sessions exist: use most recent one
   * - If no sessions: create a new one automatically
   */
  const handleShareClick = useCallback(async () => {
    const existingSession = getMostRecentSession();

    if (existingSession) {
      // Use existing session
      logger.info('CollectionShare', 'Using existing critique session for share', {
        sessionId: existingSession.id,
        collectionId,
      });
      setActiveCritiqueSession({
        id: existingSession.id,
        slug: existingSession.slug,
      });
      setIsShareDialogOpen(true);
      return;
    }

    // No sessions exist - create one automatically
    setIsCreatingSession(true);
    try {
      logger.info('CollectionShare', 'Creating new critique session for share', {
        collectionId,
        collectionName,
      });

      const session = await createCritiqueSession({
        collectionId,
        title: `Discussion of ${collectionName}`,
        intent: 'open',
        feedbackOptions: ['general'], // Default to general feedback
      });

      if (session) {
        // Refetch to update session list (use actual GraphQL operation names)
        apolloClient.refetchQueries({ include: ['GetCollection', 'GetCritiqueSessions'] });

        logger.info('CollectionShare', 'Critique session created for share', {
          sessionId: session.id,
          collectionId,
        });

        setActiveCritiqueSession({
          id: session.id,
          slug: session.slug,
        });
        setIsShareDialogOpen(true);
      }
    } catch (error) {
      logger.error('CollectionShare', 'Failed to create critique session for share', {
        error,
        collectionId,
      });
      // Could show a toast here, but for now just log the error
    } finally {
      setIsCreatingSession(false);
    }
  }, [collectionId, collectionName, createCritiqueSession, apolloClient, getMostRecentSession]);

  /**
   * Handle share dialog close
   */
  const handleShareDialogClose = useCallback(() => {
    setIsShareDialogOpen(false);
    setActiveCritiqueSession(null);
  }, []);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleShareClick}
        disabled={isCreatingSession}
        title="Share collection for discussion"
        className={className}
      >
        <ShareIcon className="mr-1.5" />
        {isCreatingSession ? 'Creating...' : 'Share'}
      </Button>

      {/* Share dialog - only render when we have an active session */}
      {activeCritiqueSession && (
        <ShareDialog
          open={isShareDialogOpen}
          onClose={handleShareDialogClose}
          critiqueSessionId={activeCritiqueSession.id}
          collectionSlug={collectionSlug}
        />
      )}
    </>
  );
}

export default CollectionShareButton;
